import { getAuth } from 'firebase/auth';

const PRODUCTION_ASSISTANT_ORIGIN = 'https://api.signalbridges.com';

export function assistantApiUrl(path, hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const envOrigin = String(
    (typeof import.meta !== 'undefined' && import.meta.env.VITE_AI_API_ORIGIN) || ''
  ).replace(/\/$/, '');
  if (envOrigin) return `${envOrigin}${path}`;
  if (hostname === 'app.signalbridges.com' || hostname.endsWith('.signalbridges.com')) {
    return `${PRODUCTION_ASSISTANT_ORIGIN}${path}`;
  }
  return path;
}

function readAssistantError(body, status) {
  const nested = body && typeof body.data === 'object' ? body.data : {};
  const message = body?.message || body?.error || nested?.message || nested?.error;
  if (typeof message === 'string' && message.trim()) return message;
  if (status === 504 || status === 502) {
    return 'The assistant took too long to answer. Try sending again.';
  }
  if (status) return `Assistant unavailable (${status})`;
  return 'Assistant error';
}

async function authHeader() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export async function fetchAiStatus() {
  try {
    const headers = await authHeader();
    const resp = await fetch(assistantApiUrl('/api/v2/ai/status'), { headers });
    if (!resp.ok) return { enabled: false };
    const body = await resp.json();
    return body.data || { enabled: false };
  } catch {
    return { enabled: false };
  }
}

export async function createAiConversation({ mode, pins, tenantId, operatorNotes, pageContext, route }) {
  const headers = await authHeader();
  const payload = { mode, pins, tenantId, operatorNotes };
  const safeRoute = route || pageContext?.route;
  if (pageContext || safeRoute) {
    payload.pageContext = pageContext || { route: safeRoute };
  }
  const resp = await fetch(assistantApiUrl('/api/v2/ai/conversations'), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(body.message || body.error || 'Failed to start assistant');
  }
  return body.data;
}

export async function updateAiConversation({ conversationId, tenantId, pins, mode }) {
  const headers = await authHeader();
  const payload = { tenantId };
  if (pins !== undefined) payload.pins = pins;
  if (mode) payload.mode = mode;
  const resp = await fetch(assistantApiUrl(`/api/v2/ai/conversations/${conversationId}`), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(body.message || body.error || 'Failed to update assistant');
  }
  return body.data;
}

export async function sendAiMessage({ conversationId, message, tenantId, operatorNotes, onEvent }) {
  const headers = await authHeader();
  headers.Accept = 'text/event-stream';
  const resp = await fetch(assistantApiUrl(`/api/v2/ai/conversations/${conversationId}/messages`), {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, tenantId, operatorNotes }),
  });
  const ctype = resp.headers.get('content-type') || '';
  if (ctype.includes('text/event-stream') && resp.body) {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let finalPayload = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split('\n\n');
      buf = parts.pop() || '';
      for (const block of parts) {
        const ev = parseSse(block);
        if (!ev) continue;
        if (onEvent) onEvent(ev.event, ev.data);
        if (ev.event === 'done') finalPayload = ev.data;
        if (ev.event === 'error') {
          throw new Error(readAssistantError(ev.data, resp.status));
        }
      }
    }
    if (!finalPayload) {
      throw new Error('The assistant did not finish. Try sending again.');
    }
    return finalPayload;
  }
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(readAssistantError(body, resp.status));
  }
  return body.data?.result || body.data;
}

function parseSse(block) {
  let event = 'message';
  const dataLines = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  let data = dataLines.join('\n');
  try {
    data = JSON.parse(data);
  } catch {
    /* keep string */
  }
  return { event, data };
}

export const OPEN_ASSISTANT_EVENT = 'signalbridge:open-assistant';

export function openAssistant(detail = {}) {
  window.dispatchEvent(new CustomEvent(OPEN_ASSISTANT_EVENT, { detail }));
}

export async function sendAssistantFeedback({ conversationId, tenantId, rating, messageId, reason, category }) {
  const headers = await authHeader();
  const payload = { tenantId, rating };
  if (messageId) payload.messageId = messageId;
  if (reason) payload.reason = reason;
  if (category) payload.category = category;
  const resp = await fetch(assistantApiUrl(`/api/v2/ai/conversations/${conversationId}/feedback`), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(body.message || body.error || 'Failed to send feedback');
  }
  return body.data;
}

export async function createAssistantSupportReport({
  conversationId,
  tenantId,
  confirm,
  title,
  description,
  type,
  route,
}) {
  const headers = await authHeader();
  const resp = await fetch(assistantApiUrl(`/api/v2/ai/conversations/${conversationId}/support-report`), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      tenantId,
      confirm,
      title,
      description,
      type,
      route,
    }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(body.message || body.error || 'Failed to send to support');
  }
  return body.data;
}
